import { useRef } from 'react';
import { Message, Agent } from '../types';
import { AgentService } from '../services/AgentService';

export const useMessageHandler = (
  agentService: AgentService,
  agents: Agent[],
  selectedAgent: string,
  sessionIds: Record<string, string>,
  agentMessages: Record<string, Message[]>,
  setAgentMessages: React.Dispatch<
    React.SetStateAction<Record<string, Message[]>>
  >,
  setTypingAgents: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >,
  extraContext?: Record<string, string>,
  onMessageSent?: () => void,
) => {
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleSendMessage = async (message: string, overrideSessionId?: string): Promise<void> => {
    onMessageSent?.();
    const currentMessages = agentMessages[selectedAgent] || [];
    const newMessage: Message = {
      id: currentMessages.length + 1,
      type: 'user',
      content: message,
      timestamp: new Date(),
      status: 'sent',
    };

    setAgentMessages(prev => ({
      ...prev,
      [selectedAgent]: [...currentMessages, newMessage],
    }));
    setTypingAgents(prev => ({ ...prev, [selectedAgent]: true }));

    try {
      const agentApiName =
        agents.find(a => a.name === selectedAgent)?.apiName || selectedAgent;
      const sessionId = overrideSessionId || sessionIds[agentApiName];

      abortControllerRef.current = new AbortController();
      const response = await agentService.sendMessage(
        message,
        agentApiName,
        sessionId,
        abortControllerRef.current.signal,
        extraContext,
      );

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.statusText}`);
      }

      await handleStreamingResponse(response);
    } catch (err) {
      console.error('Error sending message:', err);
      addErrorMessage();
    } finally {
      setTypingAgents(prev => ({ ...prev, [selectedAgent]: false }));
      abortControllerRef.current = null;
    }
  };

  const handleStreamingResponse = async (response: Response) => {
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (reader) {
      let buffer = '';
      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.substring(6));
              const { event_type, content } = data;

              if (event_type === 'error') {
                addErrorMessage();
                return;
              }
              if (event_type === 'tool_call') {
                addFunctionMessage(content);
              }
              if (event_type === 'final_message') {
                addCompleteMessage(content || 'Response received');
              }
              if (event_type === 'search_results') {
                addCompleteMessage(
                  content?.length > 0
                    ? `Top ${content.length} knowledge base documents:`
                    : 'No results found',
                  content,
                );
              }
            } catch {
              // skip malformed lines
            }
          }
        }
      } finally {
        reader.releaseLock();
      }
    }
  };

  const addErrorMessage = () => {
    setAgentMessages(prev => {
      const messages = prev[selectedAgent] || [];
      const errorMessage: Message = {
        id: messages.length + 1,
        type: 'agent',
        content:
          'Sorry, I had trouble processing your message. Please try again later.',
        timestamp: new Date(),
        agentType: selectedAgent,
        status: 'delivered',
      };
      return {
        ...prev,
        [selectedAgent]: [...messages, errorMessage],
      };
    });
  };

  const addFunctionMessage = (functionName: string) => {
    setAgentMessages(prev => {
      const messages = prev[selectedAgent] || [];
      const functionMessage: Message = {
        id: messages.length + 1,
        type: 'agent',
        content: `🔧 Tool: ${functionName}`,
        timestamp: new Date(),
        agentType: selectedAgent,
        status: 'delivered',
        isFunction: true,
      };
      return {
        ...prev,
        [selectedAgent]: [...messages, functionMessage],
      };
    });
  };

  const addCompleteMessage = (content: string, searchResults?: any[]) => {
    setAgentMessages(prev => {
      const messages = prev[selectedAgent] || [];
      const completeMessage: Message = {
        id: messages.length + 1,
        type: 'agent',
        content,
        timestamp: new Date(),
        agentType: selectedAgent,
        status: 'delivered',
        ...(searchResults ? { searchResults } : {}),
      };
      return {
        ...prev,
        [selectedAgent]: [...messages, completeMessage],
      };
    });
  };

  const abortCurrentStream = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setTypingAgents(prev => ({ ...prev, [selectedAgent]: false }));
    }
  };

  return { handleSendMessage, abortCurrentStream };
};
